import { useRouterState } from '@tanstack/react-router'
import {
  isEntryResult,
  isPineconeResult,
  NaturalLanguageResponseSegment,
  useAIEvents,
} from 'features/cantileverAISystem'
import { useCantileverAlerts } from 'features/cantileverAlerts'
import {
  ChatbotAttachmentProvider,
  useChatbotAttachments,
  useChatbotAttachmentsOptional,
} from 'features/chatbot/contexts/ChatbotAttachmentContext'
import { useChatbotContext } from 'features/chatbot/hooks/useChatbotContext'
import { useGeckoPermissions } from 'features/permissions'
import React, { createContext, useRef, useState } from 'react'

export interface Source {
  id: string
  text: string
  data: Record<string, unknown>
}

export type ConversationMessage =
  | ConversationThought
  | ConversationError
  | ConversationAIResponse
  | ConversationUserMessage

interface ConversationThought {
  type: 'thought'
  text: string
}

interface ConversationError {
  type: 'error'
  text: string
}

interface ConversationAIResponse {
  type: 'aiResponse'
  naturalLanguageResponseBySegments: NaturalLanguageResponseSegment[]
  sources?: Source[]
}

interface ConversationUserMessage {
  type: 'userMessage'
  text: string
}

interface ChatbotDataContextProps {
  conversation: ConversationMessage[]
  setConversation: React.Dispatch<React.SetStateAction<ConversationMessage[]>>
  isDelphiThinking: boolean
  sendDelphiMessage: (message: string) => void
  sendDelphiSupportRequest: (
    issueType: string,
    issueDescription: string
  ) => void
}

export const ChatbotDataContext = createContext<ChatbotDataContextProps>(null!)

const ChatbotDataLayerInner: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const sessionId = useRef<string | undefined>(undefined)
  const [conversation, setConversation] = useState<ConversationMessage[]>([])
  const { useOnDelphiResponse, promptDelphi } = useAIEvents()
  const [isDelphiThinking, setIsDelphiThinking] = useState(false)
  const routerState = useRouterState()
  const search = routerState.location.search
  const { user } = useGeckoPermissions()
  const sessionID = useRef<string | undefined>(undefined)
  const { sendDelphiSupportEmail } = useCantileverAlerts()
  const streamingSources = useRef<Source[]>([])
  const contextInfo = useChatbotContext(search)
  const attachmentContext = useChatbotAttachments()

  useOnDelphiResponse((event) => {
    appendMessage({
      type: 'aiResponse',
      naturalLanguageResponseBySegments:
        event.data.naturalLanguageResponseBySegments || [],
      sources:
        streamingSources.current.length > 0
          ? streamingSources.current
          : undefined,
    })
    setIsDelphiThinking(false)
    streamingSources.current = []
  })

  const sendDelphiSupportRequest = (
    issueType: string,
    issueDescription: string
  ) => {
    if (user?.email !== undefined) {
      sendDelphiSupportEmail(
        user?.email,
        sessionID.current || '',
        issueType,
        issueDescription
      )
    }
  }

  const appendMessage = (message: ConversationMessage) => {
    setConversation((prev) => [...prev, message])
  }

  const buildAttachmentContextString = (): string => {
    const attachments = attachmentContext?.attachments ?? []
    if (attachments.length === 0) {
      return ''
    }
    const attachmentList = attachments
      .map((a) => {
        const encodedType = a.type ? JSON.stringify(a.type) : '"unknown"'
        return `- ${a.name} (id: ${a.id}, type: ${encodedType})`
      })
      .join('\n')
    return (
      `\n\nThe following documents are attached as context. ` +
      `They are already embedded in Pinecone and can be referenced by their document IDs:\n${attachmentList}`
    )
  }

  const sendDelphiMessage = async (message: string) => {
    streamingSources.current = []

    const attachmentContextString = buildAttachmentContextString()

    promptDelphi({
      enrichedPrompt: `The user has asked you a question: ${message}. Respond to the user's question.
        
        Here is some automatically extracted page context:
        ${JSON.stringify(contextInfo, null, 2)}${attachmentContextString}
        `,
      originalUserPrompt: message,
      sessionId: sessionId.current,
      onStream: (event) => {
        if (event.type === 'thought' || event.type === 'tool_call') {
          appendMessage({
            type: 'thought',
            text: event.content,
          })
        } else if (event.type === 'tool_result') {
          const content = event.content
          if (isEntryResult(content) && content.entries) {
            const entrySources = content.entries.map((entry, index) => ({
              id: `entry-${Date.now()}-${index}`,
              text:
                entry.data.visible_name ||
                entry.data.filename ||
                entry.type ||
                `Entry ${index + 1}`,
              data: entry,
            }))
            streamingSources.current = [
              ...streamingSources.current,
              ...entrySources,
            ]
          }

          if (isPineconeResult(content) && content.results) {
            const docSources = content.results.map((result, index) => ({
              id: `doc-${Date.now()}-${index}`,
              text:
                result.visible_name || result.type || `Document ${index + 1}`,
              data: result.metadata || {},
            }))
            streamingSources.current = [
              ...streamingSources.current,
              ...docSources,
            ]
          }
        } else if (event.type === 'error') {
          appendMessage({ type: 'error', text: event.error })
        }
      },
      onError: (error) => {
        appendMessage({
          type: 'error',
          text: error,
        })
      },
    })
    appendMessage({ type: 'userMessage', text: message })
    setIsDelphiThinking(true)
  }

  return (
    <ChatbotDataContext.Provider
      value={{
        conversation,
        setConversation,
        isDelphiThinking,
        sendDelphiMessage,
        sendDelphiSupportRequest,
      }}>
      {children}
    </ChatbotDataContext.Provider>
  )
}

export const ChatbotDataLayer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const existingAttachmentContext = useChatbotAttachmentsOptional()

  if (existingAttachmentContext === undefined) {
    return (
      <ChatbotAttachmentProvider>
        <ChatbotDataLayerInner>{children}</ChatbotDataLayerInner>
      </ChatbotAttachmentProvider>
    )
  }

  return <ChatbotDataLayerInner>{children}</ChatbotDataLayerInner>
}
